'use strict';
// import isEqual from 'lodash/isEqual';
import isString from 'lodash/isString';
import {Observable} from 'rxjs/Observable';
import 'rxjs/add/observable/combineLatest';
import 'rxjs/add/observable/empty';
import 'rxjs/add/observable/from';
import 'rxjs/add/observable/fromPromise';
import 'rxjs/add/observable/merge';
import 'rxjs/add/observable/of';
import 'rxjs/add/operator/catch';
import 'rxjs/add/operator/distinctUntilChanged';
import 'rxjs/add/operator/filter';
import 'rxjs/add/operator/map';
import 'rxjs/add/operator/switchMap';
import 'rxjs/add/operator/takeUntil';
import {getId} from '../../../core-utilities/domain/ReduxUtils';
import {epicErrorHandler} from '../../../core-utilities/rx-utils';
import {firebaseAuthSuccess} from '../../../auth/actions';
import {
  assignOrganizationToModel,
  assignOrganizationProfileToModel,
  assignProjectToModel,
  assignAssetToModel,
  assignAssetProjectToModel,
  addToLastAccessedOrganizations,
  addToLastAccessedProjects,
  addToLastAccessedAssets,
} from '../actions';
import {toStringStatic as orgToString} from '../../../organizations/domain/OrganizationDomain';
import {constructAssetProjectId, toStringStatic as projectToString} from '../../../projects/domain/ProjectDomain';
import {ASSET_PROJECT_COLLECTION} from '../../../projects/domain/AssetProjectDomain';
import {toStringStatic as assetToString} from '../../../assets/domain/AssetDomain';
import {logger} from './logger';

export const epics = [
  onProjectSelected,
  onAssetSelected,
  onPrjOrAstChanged,
  onOrganizationSelected,
];

onProjectSelected.$inject = onAssetSelected.$inject = onPrjOrAstChanged.$inject = onOrganizationSelected.$inject = [
  'UserDomain',
  'PersonDomain',
  'UserPreferenceDomain',
  'FirebaseUtil',
  'AssetProjectDomain',
];

export function onProjectSelected(action$, store, dependencies) {
  return buildLastAccessedEpic(action$,
    store,
    dependencies,
    'onProjectSelected',
    assignProjectToModel.TYPE,
    'project',
    projectToString,
    addToLastAccessedProjects,
  );
}

export function onAssetSelected(action$, store, dependencies) {
  return buildLastAccessedEpic(action$,
    store,
    dependencies,
    'onAssetSelected',
    assignAssetToModel.TYPE,
    'asset',
    assetToString,
    addToLastAccessedAssets,
  );
}

export function onPrjOrAstChanged(action$, store, dependencies) {
  return Observable.combineLatest(
    action$.ofType(assignProjectToModel.TYPE),
    action$.ofType(assignAssetToModel.TYPE),
    (projectAction, assetAction) => ({
      projectId: getId(projectAction.payload.project),
      assetId: getId(assetAction.payload.asset),
    })
  )
    .distinctUntilChanged(null, ({projectId, assetId}) => `${projectId}|${assetId}`)
    .switchMap(({projectId, assetId}) => {
      logger.info(`REDUX::onPrjOrAstChanged::{project:${!!projectId},asset:${!!assetId}`);

      if (!projectId || !assetId) {
        return Observable.of(assignAssetProjectToModel(null));
      }

      const assetProjectId = constructAssetProjectId(projectId, assetId);
      const assetProjectRef = dependencies.FirebaseUtil.firebaseRef(`${ASSET_PROJECT_COLLECTION}/${assetProjectId}`);
      // TODO: will need retry logic here in case of security exceptions
      // Technically, this results in 2 value listeners on the same path in firebase, 1 here, 1 from AngularDomain
      return assetProjectRef
        .observe('value')
        // TODO: after testing this without the retry logic, try again *with* retry as rxjs5 should have fixed retryable shared observables
        // .retryWhen(function (attempts) {
        //   // logger.debug('starting retry logic to get asset project')
        //   let i = 1;
        //   return attempts
        //     .mergeMap((error) => {
        //       logger.error({err: error}, 'retrying to get asset project');
        //       return Observable.timer(i++ * 500)
        //     });
        // })
        //
        // redux version start
        // .distinctUntilChanged((prev, nxt) => isEqual(prev, nxt))
        // .map((ss) => assignAssetProjectToModel({...ss.val(),id:ss.key(),collection: ASSET_PROJECT_COLLECTION}))
        // redux version end
        //
        // AngularDomain version start
        .distinctUntilChanged(null, (ss) => ss.exists())
        .switchMap((ss) => ss.exists() ? dependencies.AssetProjectDomain.$get(assetProjectId) : Observable.of(null))
        .map(assignAssetProjectToModel)
        // AngularDomain version end
      ;
    })
    .catch(epicErrorHandler(logger, 'onPrjOrAstChanged'));
}

export function onOrganizationSelected(action$, store, dependencies) {
  return action$.ofType(firebaseAuthSuccess.TYPE)
    .map(action => action.payload.userId)
    .filter(id => !!id)
    .switchMap(userId =>
      action$.ofType(assignOrganizationToModel.TYPE)
        .switchMap((action) => {
          const orgId = getId(action.payload.organization);
          if (orgId) {
            dependencies.UserPreferenceDomain.setLastOrgId(userId, orgId);

            const orgProfile$ = Observable.fromPromise(dependencies.PersonDomain.getOrganizationProfile(userId, orgId))
              .map(assignOrganizationProfileToModel);
            const orgLabel = orgToString(action.payload.organization);
            return isString(orgLabel)
              ? Observable.merge(
                Observable.of(addToLastAccessedOrganizations({id: orgId, label: orgLabel})),
                orgProfile$,
              )
              : orgProfile$;
          } else {
            return Observable.of(addToLastAccessedOrganizations(null));
          }
        })
    )
    .catch(epicErrorHandler(logger, 'onOrganizationSelected'));
}

/**
 * @param {ActionsObservable<ReduxAction>} action$
 * @param {Store} store
 * @param dependencies
 * @param {String} name Name of the function, for logging purposes
 * @param {String} type Action type for the assignment to watch
 * @param {String} prop Name of the property to access the correct data from the assignment action payload
 * @param {Function} toLabel Function to convert the model to a user displayable string
 * @param {Function} addToLastAction Action creator for the actions to dispatch
 * @return {function(*): Observable<R>}
 */
function buildLastAccessedEpic(
  action$,
  store,
  dependencies,
  name,
  type,
  prop,
  toLabel,
  addToLastAction,
) {
  return action$.ofType(firebaseAuthSuccess.TYPE)
    .map(action => action.payload.userId)
    .filter(id => !!id)
    .switchMap(userId =>
      action$.ofType(assignOrganizationToModel.TYPE)
        .map(action => getId(action.payload.organization))
        .filter(id => !!id)
        .switchMap(orgId =>
          action$.ofType(type)
            .map(action => {
              const assignedData = action.payload[prop];
              const id = getId(assignedData);

              if (id) {
                dependencies.UserDomain.$getAppStateRelationRef(userId, orgId)
                  .child(prop)
                  .set(id);
              }

              return addToLastAction(orgId, id ? {id, label: toLabel(assignedData)} : null);
            })
        )
    )
    .catch(epicErrorHandler(logger, name));
}



// WEBPACK FOOTER //
// ./src/js/app/model/epics/assignedToModel.js